import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const STATUS_COLORS = {
  succeeded: 'var(--success)', failed: 'var(--danger)', running: 'var(--warning)',
  queued: 'var(--accent)', cancelled: 'var(--text-muted)', interrupted: 'var(--danger)',
};
const GATE_COLORS = { passed: 'var(--success)', blocked: 'var(--danger)', error: 'var(--warning)' };

export function Pill({ label, color }) {
  const c = color || 'var(--text-muted)';
  return <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: `${c}22`, color: c, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{label}</span>;
}
export function fmtDate(v) { if (!v) return '—'; try { return new Date(v).toLocaleString(); } catch { return v; } }

export function fmtEta(sec) {
  if (sec == null) return '';
  if (sec < 90) return '~1 min left';
  return `~${Math.round(sec / 60)} min left`;
}

export function ProgressBar({ progress, status, compact }) {
  const pct = Math.max(0, Math.min(100, Number(progress?.percent ?? 0)));
  const color = status === 'queued' ? 'var(--accent)' : 'var(--warning)';
  return (
    <div style={{ minWidth: compact ? 120 : 220 }}>
      <div style={{ height: 6, borderRadius: 999, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width .6s' }} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3, whiteSpace: 'nowrap' }}>
        {pct}%{progress?.eta_seconds != null ? ` · ${fmtEta(progress.eta_seconds)}` : ''}
      </div>
    </div>
  );
}

function TriggerModal({ onClose, onQueued }) {
  const [form, setForm] = useState({ mr_url: '', release: '', jira_key: '', force: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const res = await axios.post('/risk-analysis/runs', { ...form, label: 'manual from UI' });
      onQueued(res.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to queue the analysis'); }
    finally { setBusy(false); }
  }
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header"><h3>Analyse a merge request</h3></div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">MR URL (GitLab MR or Gitea PR)</label>
              <input className="form-input" required value={form.mr_url} onChange={e => setForm({ ...form, mr_url: e.target.value })} placeholder="https://exotel.vegaops.com:64998/AMEYO_STACK/CC/-/merge_requests/2178" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Release (optional)</label>
                <input className="form-input" value={form.release} onChange={e => setForm({ ...form, release: e.target.value })} placeholder="Pre-Merge Check" />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Blank files the analysis under “Pre-Merge Check”. Give the product version (6.10.3) to file by release.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Jira key (optional)</label>
                <input className="form-input" value={form.jira_key} onChange={e => setForm({ ...form, jira_key: e.target.value })} placeholder="EN-1234" />
              </div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={form.force} onChange={e => setForm({ ...form, force: e.target.checked })} />
              Re-run even if this MR was analysed already
            </label>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
              The analyst publishes a Confluence page, writes the risk ledger, posts the Jira risk comment and sends the Chat doorbell, exactly like a CI-triggered run.
            </p>
            {error && <div className="error-msg">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Queuing…' : 'Queue analysis'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function RiskRunsList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTrigger, setShowTrigger] = useState(false);
  const timerRef = useRef(null);
  const status = params.get('status') || '';
  const page = Number(params.get('page') || 1);

  const fetchRuns = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const q = new URLSearchParams({ page, limit: 50 });
      if (status) q.set('status', status);
      const [r, h] = await Promise.all([axios.get(`/risk-analysis/runs?${q}`), axios.get('/risk-analysis/health')]);
      setRuns(r.data.data || []); setTotal(r.data.total || 0); setHealth(h.data);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load runs'); }
    finally { if (!silent) setLoading(false); }
  }, [page, status]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);
  const hasActive = runs.some(r => r.status === 'running' || r.status === 'queued');
  useEffect(() => {
    if (!hasActive) return undefined;
    timerRef.current = setInterval(() => fetchRuns(true), 5000);
    return () => clearInterval(timerRef.current);
  }, [hasActive, fetchRuns]);

  async function cancel(e, id) {
    e.stopPropagation();
    if (!window.confirm('Cancel this analysis?')) return;
    try { await axios.post(`/risk-analysis/runs/${id}/cancel`); fetchRuns(true); }
    catch (err) { alert(err.response?.data?.error || 'Failed to cancel'); }
  }

  return (
    <div>
      <div className="page-header">
        <h2>Regression Risk</h2>
        <p>Pre-merge regression-risk analyses of merge requests, triggered from CI or by hand. Gate: blocked when any open High-priority risk was introduced by the MR. <Link to="/risk-analysis/docs">How to integrate with CI →</Link></p>
      </div>
      <div className="page-body">
        {health && (
          <div className="card" style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16, fontSize: 13 }}>
            <span><Pill label={health.ok ? 'worker online' : 'no worker'} color={health.ok ? 'var(--success)' : 'var(--danger)'} /></span>
            <span>Queued <strong>{health.queued}</strong></span>
            <span>Running <strong>{health.running}</strong></span>
            <span>Today <strong>${Number(health.today_spend_usd || 0).toFixed(2)}</strong>{health.daily_cost_cap_usd ? ` / $${Number(health.daily_cost_cap_usd).toFixed(2)} cap` : ''}</span>
            {health.workers?.map(w => (
              <span key={w.worker_id} style={{ color: 'var(--text-muted)' }} title={w.note || ''}>
                {w.worker_id} · {w.version} · {w.alive ? `seen ${w.seconds_since_seen}s ago` : 'offline'}{w.note ? ` · ${w.note}` : ''}
              </span>
            ))}
          </div>
        )}
        <div className="toolbar">
          <select className="form-input" style={{ width: 160 }} value={status} onChange={e => { const n = new URLSearchParams(params); if (e.target.value) n.set('status', e.target.value); else n.delete('status'); n.delete('page'); setParams(n); }}>
            <option value="">All statuses</option>
            {['queued', 'running', 'succeeded', 'failed', 'cancelled', 'interrupted'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="spacer" />
          {hasActive && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 10 }}>● live — refreshing every 5s</span>}
          <Link className="btn btn-secondary" style={{ marginRight: 8 }} to="/risk-analysis/docs">CI integration docs</Link>
          <button className="btn btn-primary" style={{ marginRight: 8 }} onClick={() => setShowTrigger(true)}>Analyse an MR…</button>
          <button className="btn btn-secondary" onClick={() => fetchRuns()} disabled={loading}>Refresh</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
        {loading ? <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
          : runs.length === 0 ? <div className="empty-state"><h3>No analyses yet</h3><p>Trigger one from a pipeline with an API key, or use “Analyse an MR…”.</p></div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
                    <th style={{ padding: '8px 10px' }}>Queued</th>
                    <th style={{ padding: '8px 10px' }}>Repository · MR</th>
                    <th style={{ padding: '8px 10px' }}>Release</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                    <th style={{ padding: '8px 10px' }}>Gate</th>
                    <th style={{ padding: '8px 10px' }}>Progress / summary</th>
                    <th style={{ padding: '8px 10px' }}>By</th>
                    <th style={{ padding: '8px 10px' }}>Cost</th>
                    <th style={{ padding: '8px 10px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', fontSize: 13 }} onClick={() => navigate(`/risk-analysis/runs/${r.id}`)}>
                      <td style={{ padding: 10, whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                      <td style={{ padding: 10 }}><div>{r.repo_full_name} <strong>!{r.mr_id}</strong></div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.jira_key || ''}{r.head_sha ? ` · ${r.head_sha.slice(0, 10)}` : ''}</div></td>
                      <td style={{ padding: 10 }}>{r.release}</td>
                      <td style={{ padding: 10 }}><Pill label={r.cancel_requested && r.status === 'running' ? 'stopping' : r.status} color={STATUS_COLORS[r.status]} /></td>
                      <td style={{ padding: 10 }}>{r.gate ? <Pill label={r.gate} color={GATE_COLORS[r.gate]} /> : '—'}</td>
                      <td style={{ padding: 10, maxWidth: 420 }} title={r.summary || r.phase || ''}>
                        {r.terminal
                          ? <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.summary || r.error || ''}</div>
                          : <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><ProgressBar progress={r.progress} status={r.status} compact /><span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>{r.phase || ''}</span></div>}
                      </td>
                      <td style={{ padding: 10, fontSize: 12 }}>{r.triggered_by}</td>
                      <td style={{ padding: 10 }}>${Number(r.cost_usd || 0).toFixed(2)}</td>
                      <td style={{ padding: 10 }}>{!r.terminal && <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--danger)' }} disabled={r.cancel_requested} onClick={e => cancel(e, r.id)}>Cancel</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {total > 50 && (
                <div className="toolbar" style={{ marginTop: 10 }}>
                  <button className="btn btn-secondary" disabled={page <= 1} onClick={() => { const n = new URLSearchParams(params); n.set('page', page - 1); setParams(n); }}>Previous</button>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 12px' }}>Page {page} of {Math.ceil(total / 50)}</span>
                  <button className="btn btn-secondary" disabled={page * 50 >= total} onClick={() => { const n = new URLSearchParams(params); n.set('page', page + 1); setParams(n); }}>Next</button>
                </div>
              )}
            </div>
          )}
      </div>
      {showTrigger && <TriggerModal onClose={() => setShowTrigger(false)} onQueued={run => { setShowTrigger(false); navigate(`/risk-analysis/runs/${run.id}`); }} />}
    </div>
  );
}
